import { Button, Kind } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { PermissionPayload, rolesApi } from 'api/roles'
import EditNavbar from 'components/Navbar/EditNavbar'
import { RoleBadge } from 'components/RoleBadge'
import Section from 'components/Section'
import Spinner from 'components/Spinner'
import { ADMIN_ROLE_NAME, getRoleBadgeVariant } from 'constants/roles'
import { useDisplayMessage } from 'hooks/useDisplayMessage'
import useFlag from 'hooks/useFlag'
import { useIsRbacAdmin } from 'hooks/useIsRbacAdmin'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap-five'
import { useDispatch } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { RouteComponentProps } from 'react-router-dom'
import { push } from 'react-router-redux'
import { merchantTheme } from 'theme'
import { NavbarTitle } from './NavbarTitle'
import { PermissionValue } from './PermissionRow'
import { LockedResourceConfig, PermissionSection } from './PermissionSection'

const t = buildTranslate('dashboard_access.edit_role')

// "User access & roles" can only be granted view_and_edit by the preset Admin role.
const LOCKED_RESOURCES: Record<string, LockedResourceConfig> = {
  merchant_users: {
    lockedOptions: new Set<PermissionValue>(['view_and_edit']),
    tooltip: t('merchant_users_locked'),
  },
}

const ROLE_NAME_INPUT_MAX_WIDTH = 300

function toPermissionValue(levels: string[]): PermissionValue {
  if (levels.includes('manage') && levels.includes('view'))
    return 'view_and_edit'
  if (levels.includes('view')) return 'can_view'
  return 'no_access'
}

function toPermissionMap(
  perms: Record<string, string[]>
): Record<string, PermissionValue> {
  return Object.fromEntries(
    Object.entries(perms).map(([key, levels]) => [
      key,
      toPermissionValue(levels),
    ])
  )
}

function toPermissionPayload(
  permissions: Record<string, PermissionValue>
): PermissionPayload[] {
  return Object.entries(permissions).map(([resourceName, value]) => ({
    resource_name: resourceName,
    can_view: value === 'can_view' || value === 'view_and_edit',
    can_manage: value === 'view_and_edit',
  }))
}

type Props = RouteComponentProps<{ id?: string }>

export const EditRole = ({ match, location }: Props) => {
  const id = match.params.id
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const isNew = !id || id === 'new'

  const searchParams = new URLSearchParams(location.search)
  const templateId = searchParams.get('template')

  const { data: roleData, isLoading: roleLoading } = rolesApi.useGetRoleQuery(
    Number(id),
    { skip: isNew }
  )
  const { data: templateData, isLoading: templateLoading } =
    rolesApi.useGetRoleQuery(Number(templateId), {
      skip: !templateId,
    })
  const { data: permData, isLoading: permLoading } =
    rolesApi.useGetGroupedPermissionsQuery()

  const [createRole] = rolesApi.useCreateRoleMutation()
  const [updateRole] = rolesApi.useUpdateRoleMutation()
  const displayMessage = useDisplayMessage()

  const [roleName, setRoleName] = useState('')
  const [editingName, setEditingName] = useState(isNew)
  const [permissions, setPermissions] = useState<
    Record<string, PermissionValue>
  >({})

  const isAdmin = useIsRbacAdmin()

  const role = roleData?.role
  const isPreset = role?.preset ?? false
  const isReadOnly = isPreset || !isAdmin
  const badgeVariant = role ? getRoleBadgeVariant(role) : null
  const recoveryAiEnabled = useFlag('recovery-ai', false)
  const groupedPermissions = useMemo(() => {
    const groups = permData?.groups ?? []
    return recoveryAiEnabled
      ? groups
      : groups.filter(g => !g.resources.every(r => r.name === 'recovery_ai'))
  }, [permData, recoveryAiEnabled])

  useEffect(() => {
    if (role) setRoleName(role.display_name)
  }, [role])

  const templateRole = templateData?.role

  // Seed permissions with all known resources defaulting to 'can_view',
  // then overlay the role's actual permissions (for edit) or template
  // permissions (for create-from-template).
  // Uses groupedPermissions (flag-filtered) so hidden resources never
  // enter state or the save payload.
  useEffect(() => {
    const groups = groupedPermissions
    if (!groups.length) return

    const defaultLevel: PermissionValue =
      role || templateRole ? 'no_access' : 'can_view'
    const defaults: Record<string, PermissionValue> = {}
    groups.forEach(group => {
      group.resources.forEach(resource => {
        defaults[resource.name] = defaultLevel
      })
    })

    const visibleResources = new Set(Object.keys(defaults))
    const overlay = toPermissionMap((role ?? templateRole)?.permissions ?? {})
    for (const [key, value] of Object.entries(overlay)) {
      if (visibleResources.has(key)) {
        defaults[key] = value
      }
    }

    // Only the preset Admin role can have view_and_edit for merchant_users.
    // For all other roles (custom, templates like Location Manager, etc.),
    // constrain to can_view so the UI doesn't show a greyed-out selected radio.
    // Note: LOCKED_RESOURCES (above) prevents selecting view_and_edit via the
    // UI — this clamp handles the initial render when the value comes from
    // backend data (e.g. a template that inherited the permission).
    const isPresetAdmin = role?.preset && role?.name === ADMIN_ROLE_NAME
    if (!isPresetAdmin && defaults.merchant_users === 'view_and_edit') {
      defaults.merchant_users = 'can_view'
    }

    setPermissions(defaults)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permData, recoveryAiEnabled, role, templateRole])

  const handlePermissionChange = useCallback(
    (key: string, value: PermissionValue) => {
      setPermissions(prev => ({ ...prev, [key]: value }))
    },
    []
  )

  // Derive snake_case machine name: lowercase, replace non-alphanumeric with underscores, trim leading/trailing underscores
  const sanitizedMachineName = roleName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

  const handleSave = async () => {
    const trimmedName = roleName.trim()
    const permPayload = toPermissionPayload(permissions)

    try {
      if (isNew) {
        await createRole({
          name: sanitizedMachineName,
          display_name: trimmedName,
          permissions: permPayload,
        }).unwrap()
        displayMessage(t('save_success_created'), 'success')
      } else {
        await updateRole({
          id: Number(id),
          display_name: trimmedName,
          permissions: permPayload,
        }).unwrap()
        displayMessage(t('save_success_updated'), 'success')
      }
      dispatch(push('/merchant_users?tab=roles'))
    } catch {
      displayMessage(t('save_error'), 'danger')
    }
  }

  const isLoading =
    roleLoading || permLoading || (!!templateId && templateLoading)

  return (
    <div
      className={`full-width ${css({
        backgroundColor: merchantTheme.colors.grey05,
        minHeight: '100vh',
      })}`}
    >
      <EditNavbar title={<NavbarTitle />}>
        <LinkContainer to="/merchant_users?tab=roles">
          <Button kind={Kind.SECONDARY_REVERSE} className="mr-s">
            {t('cancel')}
          </Button>
        </LinkContainer>
        <Switch condition={!isReadOnly}>
          <Button
            kind={Kind.PRIMARY_REVERSE}
            onClick={handleSave}
            disabled={!roleName.trim() || (isNew && !sanitizedMachineName)}
          >
            {t('save')}
          </Button>
        </Switch>
      </EditNavbar>

      <Container className="mt-m pt-xs">
        <Spinner isLoading={isLoading}>
          <Row>
            <Col className="p-xl">
              {!isAdmin && (
                <div className="mb-m">
                  <Notification kind="warning" size="full">
                    <div className="d-flex p-xs align-items-center">
                      <i
                        className="fa fa-exclamation-triangle mr-xs"
                        aria-hidden="true"
                      />
                      <Text.BodyText4>
                        {t('admin_required_warning')}
                      </Text.BodyText4>
                    </div>
                  </Notification>
                </div>
              )}
              <div className="mb-l">
                {!isNew && (
                  <RoleBadge variant={badgeVariant} className="mb-xs" />
                )}
                <div className="d-flex align-items-center">
                  {editingName && !isReadOnly ? (
                    <div>
                      <Text.BodyText4 color="grey70" className="mb-xs">
                        {t('role_name_label')}
                      </Text.BodyText4>
                      <input
                        type="text"
                        value={roleName}
                        onChange={e => setRoleName(e.target.value)}
                        onBlur={() => {
                          if (roleName.trim()) setEditingName(false)
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && roleName.trim())
                            setEditingName(false)
                        }}
                        autoFocus
                        placeholder={t('role_name_placeholder')}
                        className="form-control"
                        style={{ maxWidth: ROLE_NAME_INPUT_MAX_WIDTH }}
                      />
                      {!roleName.trim() && (
                        <Text.BodyText5 color="cayenne50" className="mt-xs">
                          {t('role_name_required')}
                        </Text.BodyText5>
                      )}
                    </div>
                  ) : (
                    <Text.Header2 className="m-0">
                      {roleName || t('new_role')}
                    </Text.Header2>
                  )}
                </div>
                <Switch condition={!isReadOnly}>
                  <button
                    className="btn btn-link p-0 spearmint-70"
                    onClick={() => setEditingName(true)}
                  >
                    {t('edit_role_name')}
                  </button>
                </Switch>
              </div>

              <Section className="p-l mb-l" kind="secondary">
                <div className="d-flex align-items-start">
                  <i
                    className="fa fa-info-circle mr-s grey-50 font-size-20"
                    aria-hidden="true"
                  />
                  <div>
                    <Text.Header4 className="m-0">
                      {t('info_banner.title')}
                    </Text.Header4>
                    <Text.BodyText4 color="grey70">
                      {t('info_banner.description')}
                    </Text.BodyText4>
                  </div>
                </div>
              </Section>

              {groupedPermissions.map(group => (
                <Section key={group.group_name} className="p-l mb-m">
                  <PermissionSection
                    category={group.group_display_name}
                    groupName={group.group_name}
                    resources={group.resources}
                    permissions={permissions}
                    onChange={handlePermissionChange}
                    disabled={isReadOnly}
                    lockedResources={LOCKED_RESOURCES}
                  />
                </Section>
              ))}
            </Col>
          </Row>
        </Spinner>
      </Container>
    </div>
  )
}
